import React, { useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import SortableHeader from '../common/SortableHeader';
import StatusBadge from '../common/StatusBadge';

const OrdersTable = ({ orders, sortConfig, onSort, onManualReviewDecision }) => {
    const scrollRef = useRef(null);
    const scrollTimerRef = useRef(null);

    const stopAutoScroll = () => {
        if (scrollTimerRef.current) {
            clearInterval(scrollTimerRef.current);
            scrollTimerRef.current = null;
        }
    };

    const startAutoScroll = (direction) => {
        stopAutoScroll();
        scrollTimerRef.current = setInterval(() => {
            if (!scrollRef.current) return;
            scrollRef.current.scrollLeft += direction * 18;
        }, 16);
    };

    useEffect(() => {
        return () => stopAutoScroll();
    }, []);

    return (
        <div className="bg-white rounded-xl shadow-sm border border-slate-100 overflow-hidden hidden md:block">
            <div className="relative">
                <div
                    ref={scrollRef}
                    className="overflow-x-auto"
                    onMouseLeave={stopAutoScroll}
                >
                    <table className="min-w-[1760px] divide-y divide-slate-200">
                        <thead className="bg-slate-50">
                            <tr>
                                <SortableHeader
                                    label="ID Заказа"
                                    sortKey="id"
                                    sortConfig={sortConfig}
                                    onSort={onSort}
                                />
                                <SortableHeader
                                    label="Пользователь"
                                    sortKey="username"
                                    sortConfig={sortConfig}
                                    onSort={onSort}
                                />
                                <SortableHeader
                                    label="Получатель"
                                    sortKey="recipient"
                                    sortConfig={sortConfig}
                                    onSort={onSort}
                                />
                                <SortableHeader
                                    label="Товар / звёзды"
                                    sortKey="stars"
                                    sortConfig={sortConfig}
                                    onSort={onSort}
                                />
                                <SortableHeader
                                    label="Коды"
                                    sortKey="digitalCodes"
                                    sortConfig={sortConfig}
                                    onSort={onSort}
                                />
                                <SortableHeader
                                    label="Тип покупки"
                                    sortKey="purchaseKind"
                                    sortConfig={sortConfig}
                                    onSort={onSort}
                                />
                                <SortableHeader
                                    label="Сумма"
                                    sortKey="amount"
                                    sortConfig={sortConfig}
                                    onSort={onSort}
                                />
                                <SortableHeader
                                    label="Статус"
                                    sortKey="status"
                                    sortConfig={sortConfig}
                                    onSort={onSort}
                                />
                                <SortableHeader
                                    label="Ручная проверка"
                                    sortKey="manualReviewStatus"
                                    sortConfig={sortConfig}
                                    onSort={onSort}
                                />
                                <SortableHeader
                                    label="Источник"
                                    sortKey="source"
                                    sortConfig={sortConfig}
                                    onSort={onSort}
                                />
                                <SortableHeader
                                    label="Тип"
                                    sortKey="type"
                                    sortConfig={sortConfig}
                                    onSort={onSort}
                                />
                                <SortableHeader
                                    label="Дата"
                                    sortKey="date"
                                    sortConfig={sortConfig}
                                    onSort={onSort}
                                />
                            </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-slate-200">
                            {orders.map((order) => (
                                <tr key={order.id} className="hover:bg-slate-50 transition-colors">
                                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-indigo-600">{order.id}</td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-700">
                                        {order.userId ? (
                                            <Link to={`/users/${order.userId}`} className="text-indigo-600 hover:underline">{order.username}</Link>
                                        ) : order.username}
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-700">{order.recipient}</td>
                                    <td className="px-6 py-4 text-sm text-slate-700 max-w-[220px]">{order.productCell}</td>
                                    <td className="px-6 py-4 text-xs text-slate-700 max-w-[320px]">
                                        {order.digitalCodes ? (
                                            <div className="font-mono break-all whitespace-pre-wrap">{order.digitalCodes}</div>
                                        ) : (
                                            <span className="text-slate-400">—</span>
                                        )}
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-xs text-slate-600">{order.purchaseKindLabel}</td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm font-bold text-slate-800">{order.amount} ₽</td>
                                    <td className="px-6 py-4 whitespace-nowrap"><StatusBadge status={order.status} /></td>
                                    <td className="px-6 py-4 text-xs text-slate-600 min-w-[180px]">
                                        {order.manualReviewStatus === 'pending' ? (
                                            <div className="flex flex-col gap-2">
                                                <StatusBadge status="manual_review" />
                                                <div className="flex gap-2">
                                                    <button
                                                        type="button"
                                                        onClick={() => onManualReviewDecision?.(order, 'accept')}
                                                        className="px-2 py-1 bg-emerald-600 text-white rounded text-xs"
                                                    >
                                                        Подтвердить
                                                    </button>
                                                    <button
                                                        type="button"
                                                        onClick={() => onManualReviewDecision?.(order, 'reject')}
                                                        className="px-2 py-1 bg-rose-600 text-white rounded text-xs"
                                                    >
                                                        Отклонить
                                                    </button>
                                                </div>
                                            </div>
                                        ) : order.manualReviewStatus && order.manualReviewStatus !== 'not_required' ? (
                                            <div className="space-y-1">
                                                <StatusBadge status={order.manualReviewStatus} />
                                                {order.manualReviewByUserId ? (
                                                    <div className="text-slate-400">admin #{order.manualReviewByUserId}</div>
                                                ) : null}
                                            </div>
                                        ) : (
                                            <span className="text-slate-400">—</span>
                                        )}
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-xs text-slate-500 font-mono bg-slate-50 p-1 rounded max-w-[150px] overflow-hidden text-ellipsis">
                                        {order.source}
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-xs text-slate-600">
                                        {order.type}
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-500">{order.date}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>

                <button
                    type="button"
                    aria-label="Прокрутить влево"
                    className="absolute left-2 top-1/2 -translate-y-1/2 h-[100px] w-12 rounded-lg bg-slate-900/55 text-white text-3xl font-bold hover:bg-slate-900/75 transition-colors z-10 flex items-center justify-center"
                    onMouseEnter={() => startAutoScroll(-1)}
                    onMouseLeave={stopAutoScroll}
                >
                    ‹
                </button>
                <button
                    type="button"
                    aria-label="Прокрутить вправо"
                    className="absolute right-2 top-1/2 -translate-y-1/2 h-[100px] w-12 rounded-lg bg-slate-900/55 text-white text-3xl font-bold hover:bg-slate-900/75 transition-colors z-10 flex items-center justify-center"
                    onMouseEnter={() => startAutoScroll(1)}
                    onMouseLeave={stopAutoScroll}
                >
                    ›
                </button>
            </div>
            {orders.length === 0 && (
                <div className="p-8 text-center text-slate-500">Заказы не найдены</div>
            )}
        </div>
    );
};

export default OrdersTable;